/**
 * CustomAvatarSelo.tsx — o rosto de quem mandou, com o selo do que é.
 *
 * ─── POR QUE ESTE VIROU COMPONENTE E O CARTÃO NÃO ───────────────────────────
 * A Inbox tem dois arranjos (`page-completo` e `page-celular`) e ele perguntou
 * o que deveria virar componente (30/08/2026). A resposta não é "o cartão": o
 * cartão tem o mesmo conteúdo e arranjos diferentes, e um componente com dois
 * modos de layout vira um emaranhado de `if`.
 *
 * **Esta peça, sim.** Ela é IDÊNTICA nos dois lugares — mesmo tamanho, mesma
 * regra de queda (foto → iniciais → ícone), mesmo selo no mesmo canto. É o
 * gatilho do segundo uso da lei visual §6, na forma limpa: o que se repete
 * igual vira peça; o que se repete diferente fica em cada tela.
 *
 * ─── AS DUAS PERGUNTAS QUE ELA RESPONDE ─────────────────────────────────────
 * *"Gostei da foto da Adriana indicando que é uma mensagem. Agora eu tenho que
 * saber o que é o aviso"* — e a foto sozinha respondia uma e calava a outra.
 *
 *   o ROSTO diz QUEM      foto quando há; iniciais quando a pessoa não tem foto
 *   o SELO diz O QUÊ      o ícone do módulo, no canto inferior direito
 *
 * ⚠️ SEM PESSOA, QUEM APARECE É A MARCA DA LOJA. É o caso do aviso que o
 * sistema gerou — o Tiny caiu, a pendência venceu, o ponto não foi batido. Ele
 * pediu assim em 30/08, e a razão é que "quem mandou" continua tendo resposta:
 * foi a casa. O ícone genérico que estava aqui repetia o desenho do selo e não
 * respondia nada.
 *
 * ⚠️ O selo tem CONTRASTE TOTAL sobre a foto (fundo e traço invertem com o
 * tema, ver `--casaa-inbox-selo-*`). A primeira versão usava um pastel
 * `bg-<cor>-subtle` e o símbolo sumia sobre a imagem — dois tons claros
 * brigando. Foi reprovado olhando, e o conserto foi o contraste, não o lugar.
 */

import { CustomMarcaCasaASimbolo } from './CustomMarcasCasaA';

interface Props {
  /** URL da foto, quando existe. */
  foto?: string | null;
  /** Nome de quem mandou — vira iniciais quando não há foto. */
  nome?: string | null;
  /** Classe do ícone do módulo (`bi-chat-dots`, `bi-clipboard-check`…). */
  icone: string;
  /** O que o selo diz ao parar o mouse — o nome do evento. */
  titulo?: string;
}

function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}

export function CustomAvatarSelo({ foto, nome, icone, titulo }: Props) {
  return (
    <span className="casaa-inbox-avatar">
      {foto ? (
        /* ⚠️ SEM `coverimg`, E ESTA FOI A CAUSA DE "as fotos não carregam".
           A classe do tema faz `& > img { display: none }` e espera que o JS
           dele varra o DOM copiando o `src` para o `background-image` do pai.
           Esse varrimento roda no CARREGAMENTO DA PÁGINA — então tudo que o
           React cria depois (paginar, buscar, trocar de pasta) nasce com a
           imagem escondida e sem fundo: **avatar vazio, e para sempre**.

           Foi assim que o defeito se disfarçou de lentidão por três rodadas: a
           primeira tela funcionava, a segunda não, e a marca da loja aparecia
           sempre — porque é SVG inline e não passa por isso. Medir o servidor
           não achava nada, porque não havia nada de errado no servidor.

           `object-fit: cover` faz o mesmo recorte, no CSS, sem depender de JS
           nenhum. É o que `agendamentos.tsx` já fazia, com o motivo escrito ao
           lado — eu é que não olhei antes de copiar o molde. */
        <figure className="avatar avatar-40 rounded-circle mb-0 casaa-foto-no-avatar">
          <img src={foto} alt="" />
        </figure>
      ) : nome ? (
        <span className="avatar avatar-40 rounded-circle bg-theme-1-subtle text-theme-1 d-flex align-items-center justify-content-center">
          <small>{iniciais(nome)}</small>
        </span>
      ) : (
        /* SEM PESSOA = É O SISTEMA, e aí quem "manda" é a loja.
           Dele, 30/08: *"existem notificações que vão ser do próprio sistema —
           quando a pessoa esquecer de bater ponto, quem envia é o sistema. E aí,
           em vez da foto, deveria aparecer a logo redondinha da loja."*

           Ele está certo, e o ícone genérico que estava aqui tinha um defeito
           pior que ser feio: ele repetia o mesmo desenho do selo, dizendo duas
           vezes "isto é uma autorização" e nenhuma vez "isto veio do sistema".
           A marca responde a pergunta que o rosto responderia. */
        /* ⚠️ A MARCA É O CÍRCULO — não vai dentro de um.
           Eu a tinha posto dentro de um `avatar rounded-circle`, e o resultado
           foi bola dentro de bola: o símbolo pequeno no meio de outro disco, e
           com a cor herdada do `text-theme-1` do avatar em vez da cor da marca.
           Ele viu as duas coisas de imediato — *"não está ocupando o espaço
           inteiro da bolinha"* e *"está nas cores diferentes das que aparecem
           para mim no sistema"*.

           `CustomMarcaCasaASimbolo` já desenha o disco cheio com o Λ vazado (a
           geometria está documentada nele). Então ele É o avatar: 40px, o mesmo
           diâmetro das fotos ao lado, e a cor vem da classe — como o header já
           faz com `.casaa-icon`. É SVG, então escala sem borrar em qualquer
           densidade de tela. */
        <span className="casaa-inbox-marca">
          <CustomMarcaCasaASimbolo />
        </span>
      )}
      {/* O SELO AGORA VALE SEMPRE. Antes ele sumia quando não havia pessoa,
          porque o ícone do módulo tomava o avatar inteiro e repeti-lo seria
          dizer a mesma coisa duas vezes. Com a marca da loja no lugar, as duas
          perguntas voltam a ser diferentes: o disco diz DE ONDE veio (a loja) e
          o selo diz O QUE é (o módulo). */}
      <span className="casaa-inbox-selo" title={titulo}>
        <i className={`bi ${icone}`}></i>
      </span>
    </span>
  );
}
